'use client';

import { c as _c } from "react/compiler-runtime";
import { Deal } from '@funkey/constants';
import type { I18n } from '@funkey/locales';
import { cx } from 'class-variance-authority';
import { FormGroup, FormInputErrors, Icon, Input } from '~/components/primitives';
import Title from '~/components/utils/Title';
import { useI18n } from '~/features/i18n';
import type { FormState } from '~/modules/form';
import { CheckoutSourceFormFields } from './checkoutSource';

/** "How do you know Funkey?" — asked in the last checkout step, public portal only. */
export const CheckoutSourceFieldset: React.FC<{
  formState: FormState.Subform<CheckoutSourceFormFields.FormValues>;
}> = t0 => {
  const $ = _c(21);
  const {
    formState
  } = t0;
  const i18n = useI18n();
  let t1;
  if ($[0] !== i18n) {
    t1 = i18n._("checkout.source.question");
    $[0] = i18n;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <Title as="legend" size="xl" weight="bold" className="mb-6">{t1}</Title>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== formState || $[5] !== i18n) {
    t3 = Deal.CHECKOUT_SOURCES.map(source => <CheckoutSourceOption key={source} i18n={i18n} source={source} isSelected={formState.values.checkoutSource === source} onSelect={() => formState.handleChange("checkoutSource", source)} />);
    $[4] = formState;
    $[5] = i18n;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t3) {
    t4 = <div className="grid grid-cols-2 gap-3 md:grid-cols-3">{t3}</div>;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== formState) {
    t5 = formState.getErrorMessages("checkoutSource");
    $[9] = formState;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t5) {
    t6 = <FormInputErrors errors={t5} />;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== formState || $[14] !== i18n) {
    t7 = formState.values.checkoutSource === "OTHER" && <FormGroup htmlFor="checkoutSourceOther" variant="no_spacing"><Input id="checkoutSourceOther" placeholder={i18n._("checkout.source.other.placeholder")} maxLength={255} {...formState.getNativeInputProps("checkoutSourceOther")} /><FormInputErrors errors={formState.getErrorMessages("checkoutSourceOther")} /></FormGroup>;
    $[13] = formState;
    $[14] = i18n;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t2 || $[17] !== t4 || $[18] !== t6 || $[19] !== t7) {
    t8 = <fieldset data-form-field="" className="flex flex-col gap-6" data-testid="checkout-source-form">{t2}{t4}{t6}{t7}</fieldset>;
    $[16] = t2;
    $[17] = t4;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  return t8;
};

/** The whole tile is the click target; the radio stays in the DOM (visually hidden) so
 * keyboard and screen-reader users still get real radio-group semantics. */
const CheckoutSourceOption: React.FC<{
  i18n: I18n;
  source: Deal.CheckoutSource;
  isSelected: boolean;
  onSelect: () => void;
}> = t0 => {
  const $ = _c(33);
  const {
    i18n,
    source,
    isSelected,
    onSelect
  } = t0;
  let option;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[0] !== i18n || $[1] !== isSelected || $[2] !== onSelect || $[3] !== source) {
    option = CheckoutSourceFormFields.option(i18n, source);
    t3 = `checkoutSource-${source}`;
    t4 = `checkout-source-option-${source}`;
    const t7 = isSelected ? "border-secondary bg-secondary-50" : "border-blue-200 hover:border-blue-400 hover:bg-blue-50";
    if ($[11] !== t7) {
      t5 = cx("flex cursor-pointer select-none flex-col items-center justify-center gap-2 rounded-2xl border-2 p-5 text-center transition-colors duration-200", "focus-within:border-secondary focus-within:ring-2 focus-within:ring-secondary/30", t7);
      $[11] = t7;
      $[12] = t5;
    } else {
      t5 = $[12];
    }
    const t8 = `checkoutSource-${source}`;
    if ($[13] !== isSelected || $[14] !== onSelect || $[15] !== source || $[16] !== t8) {
      t6 = <input type="radio" id={t8} name="checkoutSource" value={source} checked={isSelected} onChange={onSelect} className="sr-only" />;
      $[13] = isSelected;
      $[14] = onSelect;
      $[15] = source;
      $[16] = t8;
      $[17] = t6;
    } else {
      t6 = $[17];
    }
    t1 = "flex h-10 items-center gap-1.5 text-secondary";
    t2 = option.icons.map(icon => <Icon key={icon} name={icon} size="lg" strokeWidth={icon === "google" ? 0 : undefined} className={cx(option.icons.length > 1 && "h-6 w-6")} />);
    $[0] = i18n;
    $[1] = isSelected;
    $[2] = onSelect;
    $[3] = source;
    $[4] = option;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    option = $[4];
    t1 = $[5];
    t2 = $[6];
    t3 = $[7];
    t4 = $[8];
    t5 = $[9];
    t6 = $[10];
  }
  let t7;
  if ($[18] !== t1 || $[19] !== t2) {
    t7 = <span className={t1}>{t2}</span>;
    $[18] = t1;
    $[19] = t2;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== option.text) {
    t8 = <span className="text-md font-medium leading-6 text-primary">{option.text}</span>;
    $[21] = option.text;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== option.hint) {
    t9 = !!option.hint && <span className="text-sm leading-5 text-blue-400">({option.hint})</span>;
    $[23] = option.hint;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== t3 || $[26] !== t4 || $[27] !== t5 || $[28] !== t6 || $[29] !== t7 || $[30] !== t8 || $[31] !== t9) {
    t10 = <label htmlFor={t3} data-testid={t4} className={t5}>{t6}{t7}{t8}{t9}</label>;
    $[25] = t3;
    $[26] = t4;
    $[27] = t5;
    $[28] = t6;
    $[29] = t7;
    $[30] = t8;
    $[31] = t9;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  return t10;
};